import {
  BookedSkatingLesson,
  BookedSkatingLessonsPaginatedResponse,
  CustomerWaiver,
  RequestedByType,
} from '@types';
import {
  ConfirmationModal,
  Pagination,
  PDFViewerModal,
  SearchBar,
} from '_components/atoms';
import {
  RemoveWaiverModal,
  SearchCustomerWaivers,
  WaiverRemovalLogModal,
} from '_components/molecules';
import AdminService from '_services/AdminService';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useEffect, useState } from 'react';
import { FaFileSignature, FaReceipt } from 'react-icons/fa';

const BookedSkatingLessons: React.FC = () => {
  const [lessons, setLessons] = useState<BookedSkatingLesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedParticipantId, setSelectedParticipantId] = useState<
    number | null
  >(null);
  const [redeemingParticipantId, setRedeemingParticipantId] = useState<
    number | null
  >(null);
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });
  const [attachWaiverModal, setAttachWaiverModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
  }>({
    isOpen: false,
    recordId: null,
  });
  const [attachedWaiver, setAttachedWaiver] = useState<CustomerWaiver | null>(
    null,
  );
  const [isAttachingWaiver, setIsAttachingWaiver] = useState(false);
  const [removeWaiverModal, setRemoveWaiverModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
  }>({
    isOpen: false,
    recordId: null,
  });
  const [isRemovingWaiver, setIsRemovingWaiver] = useState(false);
  const [waiverLogModal, setWaiverLogModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
  }>({
    isOpen: false,
    recordId: null,
  });
  const fetchLessons = useCallback(async () => {
    try {
      setLoading(true);

      const responseData = (await AdminService.getBookedSkatingLessons(
        pageNumber,
        pageSize,
        searchTerm,
      )) as unknown as BookedSkatingLessonsPaginatedResponse;

      if (responseData) {
        setLessons(responseData.lessons);
        setTotalRecords(responseData.pagination.totalRecords);
        setTotalPages(responseData.pagination.totalPages);
        setHasNext(responseData.pagination.hasNext);
        setHasPrevious(responseData.pagination.hasPrevious);
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, searchTerm]);

  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  const handleSearch = useCallback((searchTerm: string) => {
    // Only search if there are at least 3 characters or the search term is empty (to clear results)
    if (searchTerm.length >= 3 || searchTerm.length === 0) {
      setSearchTerm(searchTerm);
      setPageNumber(1); // Reset to first page when searching
    }
  }, []);

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1); // Reset to first page when changing page size
  };

  const handleRedeemLesson = (participantId: number) => {
    setSelectedParticipantId(participantId);
    setShowConfirmModal(true);
  };

  const confirmRedeemLesson = async () => {
    if (!selectedParticipantId) return;

    try {
      setRedeemingParticipantId(selectedParticipantId);
      await AdminService.redeemBookedSkatingLesson(selectedParticipantId);
      console.log('Redeeming participant:', selectedParticipantId);

      // Refresh the data
      await fetchLessons();
    } catch (error) {
      console.error('Error redeeming participant:', error);
    } finally {
      setRedeemingParticipantId(null);
    }
  };

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({
      isOpen: true,
      url,
      title,
    });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({
      isOpen: false,
      url: '',
      title: '',
    });
  };

  const openAttachWaiverModal = (recordId: number) => {
    setAttachWaiverModal({
      isOpen: true,
      recordId,
    });
    setAttachedWaiver(null);
  };

  const closeAttachWaiverModal = () => {
    setAttachWaiverModal({
      isOpen: false,
      recordId: null,
    });
    setAttachedWaiver(null);
  };

  const handleAttachWaiver = (waiver: CustomerWaiver) => {
    setAttachedWaiver(waiver);
  };

  const handleUnattachWaiver = () => {
    setAttachedWaiver(null);
  };

  const confirmAttachWaiver = async () => {
    if (
      !attachedWaiver ||
      !attachWaiverModal.recordId ||
      !attachedWaiver.pdfFilePath
    )
      return;

    try {
      setIsAttachingWaiver(true);
      await AdminService.attachCustomerWaiverToRecord(
        'skating_lesson',
        attachWaiverModal.recordId,
        attachedWaiver.id,
        attachedWaiver.pdfFilePath,
      );

      // Refresh the data
      await fetchLessons();
      closeAttachWaiverModal();
    } catch (error) {
      console.error('Error attaching waiver:', error);
    } finally {
      setIsAttachingWaiver(false);
    }
  };

  const confirmRemoveWaiver = async (
    requestedByType: RequestedByType,
    notes: string,
  ) => {
    if (!removeWaiverModal.recordId) return;

    try {
      setIsRemovingWaiver(true);
      await AdminService.removeAttachedWaiver(
        'skating_lesson',
        removeWaiverModal.recordId,
        requestedByType,
        notes || undefined,
      );
      await fetchLessons();
      setRemoveWaiverModal({ isOpen: false, recordId: null });
    } catch (error) {
      console.error('Error removing waiver:', error);
    } finally {
      setIsRemovingWaiver(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Booked Skating Lessons
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage and view all booked skating lessons
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="text-sm text-gray-600">
            Total Records: <span className="font-semibold">{totalRecords}</span>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <SearchBar
        placeholder="Search by customer name, email, participant name, or barcode ID..."
        onSearch={handleSearch}
        debounceMs={1000}
      />

      {/* Pagination Controls */}
      <Pagination
        pageNumber={pageNumber}
        totalPages={totalPages}
        pageSize={pageSize}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onReload={fetchLessons}
      />

      {/* Lessons Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lesson Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Participant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lessons.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No lessons found
                  </td>
                </tr>
              ) : (
                lessons.map(lesson => (
                  <tr key={lesson.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">
                        {lesson.title}
                      </div>
                      <div className="text-xs text-gray-700">
                        Barcode:{' '}
                        <span className="font-semibold text-blue-600">
                          {lesson.barcodeId}
                        </span>
                      </div>
                      <div className="text-xs text-gray-700">
                        Created:{' '}
                        <span className="font-semibold">
                          {HelperMethods.formatDateTimeLocal(lesson.createdAt)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {lesson.participantFirstName}{' '}
                        {lesson.participantLastName}
                      </div>
                      <div className="text-sm text-gray-500">
                        DOB:{' '}
                        {HelperMethods.formatDate(
                          lesson.participantDateOfBirth,
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {lesson.customerName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {lesson.customerEmail}
                      </div>
                      <div className="text-sm text-gray-500">
                        {lesson.customerPhone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          lesson.redeemed
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {lesson.redeemed ? 'Redeemed' : 'Not Redeemed'}
                      </span>
                      {lesson.redeemed && lesson.redeemedBy && (
                        <div className="text-xs text-gray-500 mt-1">
                          by {lesson.redeemedBy}
                        </div>
                      )}
                      {lesson.redeemed && lesson.redeemedAt && (
                        <div className="text-xs text-gray-500 mt-1">
                          Redeemed on{' '}
                          {HelperMethods.formatDateTimeLocal(lesson.redeemedAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2 justify-between">
                        {lesson.waiverUrl ? (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() =>
                                openPdfViewer(lesson.waiverUrl!, 'View Waiver')
                              }
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <FaFileSignature className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() =>
                                setRemoveWaiverModal({
                                  isOpen: true,
                                  recordId: lesson.participantId,
                                })
                              }
                              className="px-3 py-1 text-xs rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                            >
                              Remove Waiver
                            </button>
                          </div>
                        ) : lesson.waiverType === 'Paper' ? (
                          <div className="flex items-center space-x-2">
                            <div className="text-xs text-green-700">
                              <div className="font-semibold">Paper waiver</div>
                              {lesson.paperWaiverCompletedAt && (
                                <div className="text-gray-500">
                                  {HelperMethods.formatDateTimeLocal(
                                    lesson.paperWaiverCompletedAt,
                                  )}
                                </div>
                              )}
                              {lesson.paperWaiverRecordedByName && (
                                <div className="text-gray-500">
                                  by {lesson.paperWaiverRecordedByName}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() =>
                                setRemoveWaiverModal({
                                  isOpen: true,
                                  recordId: lesson.participantId,
                                })
                              }
                              className="px-3 py-1 text-xs rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                            >
                              Remove Waiver
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <div className="text-gray-400">
                              <FaFileSignature className="w-5 h-5" />
                            </div>
                            <button
                              onClick={() =>
                                openAttachWaiverModal(lesson.participantId)
                              }
                              className="text-green-600 hover:text-green-900"
                            >
                              Attach Waiver
                            </button>
                          </div>
                        )}
                        <button
                          onClick={() =>
                            setWaiverLogModal({
                              isOpen: true,
                              recordId: lesson.participantId,
                            })
                          }
                          className="px-3 py-1 text-xs rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                          Waiver Log
                        </button>
                        {lesson.receiptUrl && (
                          <button
                            onClick={() =>
                              openPdfViewer(lesson.receiptUrl!, 'View Receipt')
                            }
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <FaReceipt className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() =>
                            handleRedeemLesson(lesson.participantId)
                          }
                          disabled={
                            redeemingParticipantId === lesson.participantId ||
                            lesson.redeemed
                          }
                          className={`px-3 py-1 text-xs rounded-md transition-colors ${
                            redeemingParticipantId === lesson.participantId ||
                            lesson.redeemed
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
                          }`}
                        >
                          {redeemingParticipantId === lesson.participantId
                            ? 'Redeeming...'
                            : 'Redeem'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lessons Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {lessons.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            No lessons found
          </div>
        ) : (
          lessons.map(lesson => (
            <div
              key={lesson.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              {/* Lesson Info */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {lesson.title}
                  </h3>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      lesson.redeemed
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {lesson.redeemed ? 'Redeemed' : 'Not Redeemed'}
                  </span>
                </div>
                <div className="text-xs text-gray-700 mt-1">
                  Barcode:{' '}
                  <span className="font-semibold text-blue-600">
                    {lesson.barcodeId}
                  </span>
                </div>
                <div className="text-xs text-gray-700 mt-1">
                  Created:{' '}
                  <span className="font-semibold">
                    {HelperMethods.formatDateTimeLocal(lesson.createdAt)}
                  </span>
                </div>
              </div>

              {/* Participant Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Participant
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">
                    {lesson.participantFirstName} {lesson.participantLastName}
                  </div>
                  <div>
                    DOB:{' '}
                    {HelperMethods.formatDate(lesson.participantDateOfBirth)}
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Customer
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">{lesson.customerName}</div>
                  <div>{lesson.customerEmail}</div>
                  <div>{lesson.customerPhone}</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {lesson.waiverUrl ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        openPdfViewer(lesson.waiverUrl!, 'View Waiver')
                      }
                      className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                    >
                      <FaFileSignature className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() =>
                        setRemoveWaiverModal({
                          isOpen: true,
                          recordId: lesson.participantId,
                        })
                      }
                      className="inline-flex items-center px-3 py-2 bg-red-100 text-red-700 text-sm rounded-md hover:bg-red-200 transition-colors"
                    >
                      Remove Waiver
                    </button>
                  </div>
                ) : lesson.waiverType === 'Paper' ? (
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-green-700">
                      <div className="font-semibold">Paper waiver</div>
                      {lesson.paperWaiverCompletedAt && (
                        <div className="text-gray-500">
                          {HelperMethods.formatDateTimeLocal(
                            lesson.paperWaiverCompletedAt,
                          )}
                        </div>
                      )}
                      {lesson.paperWaiverRecordedByName && (
                        <div className="text-gray-500">
                          by {lesson.paperWaiverRecordedByName}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        setRemoveWaiverModal({
                          isOpen: true,
                          recordId: lesson.participantId,
                        })
                      }
                      className="inline-flex items-center px-3 py-2 bg-red-100 text-red-700 text-sm rounded-md hover:bg-red-200 transition-colors"
                    >
                      Remove Waiver
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => openAttachWaiverModal(lesson.participantId)}
                    className="inline-flex items-center px-3 py-2 bg-green-100 text-green-700 text-sm rounded-md hover:bg-green-200 transition-colors"
                  >
                    Attach Waiver
                  </button>
                )}
                <button
                  onClick={() =>
                    setWaiverLogModal({
                      isOpen: true,
                      recordId: lesson.participantId,
                    })
                  }
                  className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200 transition-colors"
                >
                  Waiver Log
                </button>
                {lesson.receiptUrl && (
                  <button
                    onClick={() =>
                      openPdfViewer(lesson.receiptUrl!, 'View Receipt')
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    <FaReceipt className="w-5 h-5" />
                  </button>
                )}
                {!lesson.redeemed && (
                  <button
                    onClick={() => handleRedeemLesson(lesson.participantId)}
                    disabled={redeemingParticipantId === lesson.participantId}
                    className={`inline-flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                      redeemingParticipantId === lesson.participantId
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
                    }`}
                  >
                    {redeemingParticipantId === lesson.participantId
                      ? 'Redeeming...'
                      : 'Redeem'}
                  </button>
                )}
              </div>

              {/* Redeemed By Info */}
              {lesson.redeemed && lesson.redeemedBy && (
                <div className="mt-3 text-xs text-gray-500">
                  Redeemed by: {lesson.redeemedBy}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmRedeemLesson}
        title="Redeem Lesson"
        message="Are you sure you want to redeem this lesson? This action cannot be undone."
        confirmText="Redeem"
        cancelText="Cancel"
        type="warning"
      />

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />

      {/* Attach Waiver Modal */}
      {attachWaiverModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Attach Waiver
              </h2>
              <button
                onClick={closeAttachWaiverModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <SearchCustomerWaivers
                onAttach={handleAttachWaiver}
                onUnattach={handleUnattachWaiver}
                attachedWaiverId={`${attachedWaiver?.pdfFilePath}_${attachedWaiver?.barcodeId}`}
              />
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeAttachWaiverModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAttachWaiver}
                disabled={!attachedWaiver?.pdfFilePath}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  attachedWaiver?.pdfFilePath
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isAttachingWaiver ? 'Attaching...' : 'Confirm Attach'}
              </button>
            </div>
          </div>
        </div>
      )}

      <RemoveWaiverModal
        isOpen={removeWaiverModal.isOpen}
        isRemoving={isRemovingWaiver}
        onClose={() => setRemoveWaiverModal({ isOpen: false, recordId: null })}
        onConfirm={confirmRemoveWaiver}
      />
      <WaiverRemovalLogModal
        isOpen={waiverLogModal.isOpen}
        bookingType="skating_lesson"
        recordId={waiverLogModal.recordId}
        onClose={() => setWaiverLogModal({ isOpen: false, recordId: null })}
        onViewWaiver={url => openPdfViewer(url, 'Removed Waiver')}
      />
    </div>
  );
};

export default BookedSkatingLessons;
